import {useReconcileBalanceAnchorMutation} from '@transactions/graphql';
import * as React from 'react';

/**
 * The minimal row shape a reconcile session needs — satisfied by both the
 * timeline's full `BasicRawTransactionFragment` rows and the reconcile
 * modal's lite projection.
 */
export type ReconcileSessionTransaction = {
  _id: string;
  amount: number;
  /** ISO date string; compares correctly as a string. */
  date: string;
  reconciliation?: {reconciliationId: string} | null;
};

/**
 * Whether a date falls inside the reconciliation period (baseline, anchor].
 * The window is a relevance filter only — the server accepts any unreconciled
 * transaction as long as the sum matches (late-posting rows may legitimately
 * belong to a neighboring statement).
 */
export const inReconcileWindow = (
  window: {baselineDate: string | null; anchorDate: string},
  date: string,
): boolean =>
  (window.baselineDate == null || date > window.baselineDate) &&
  date <= window.anchorDate;

/**
 * Description prefilled on the transaction created by "fix up" — a synthetic
 * row that absorbs the leftover discrepancy so the period balances. Dated the
 * recorded anchor so it always falls inside the window, and suffixed with that
 * date so repeated adjustments across periods stay distinguishable.
 */
export const fixUpDescription = (anchorDate: string): string =>
  `Reconciliation adjustment — ${anchorDate}`;

type Args<T extends ReconcileSessionTransaction> = {
  accountId: string;
  anchorId: string;
  anchorDate: string;
  anchorBalance: number;
  /**
   * The nearest prior anchor's date, or null (once resolved) when there is
   * none — the account needs a starting balance before this anchor can be
   * reconciled.
   */
  baselineDate: string | null;
  baselineBalance: number;
  /** False while the baseline lookup is still in flight. */
  baselineResolved: boolean;
  transactions: ReadonlyArray<T>;
  /** True once every page covering (baseline, anchor] has loaded. */
  windowCovered: boolean;
  loadingTransactions: boolean;
  /** The controlled selection; the session reads it, never stores its own. */
  selectedIds: ReadonlyArray<string>;
  /**
   * Called exactly once per anchor, when the window is fully loaded: seeds
   * the selection with every unreconciled in-window row. Seeding once avoids
   * wiping the user's manual adjustments when Apollo's cache-and-network
   * policy re-renders with a re-fetched list.
   */
  onSeedSelection: (transactions: T[]) => void;
  /**
   * Called on successful reconcile with the claimed row ids, so the owner
   * can surgically drop them from cached lists instead of resetting a
   * paginated query (which would throw away every loaded page).
   */
  onReconciled?: (claimedRawTransactionIds: string[]) => void;
};

/**
 * The reconciliation workflow, extracted from ReconcileAnchorModal so the
 * reconcile timeline can run the same flow in place: seed the selection with
 * the period's rows, track the running sum against the needed delta, and
 * submit the claim. Data fetching stays with the caller — the modal and the
 * timeline page differently — so this hook only consumes the loaded rows
 * plus a window-coverage signal.
 */
export const useReconcileSession = <T extends ReconcileSessionTransaction>({
  accountId,
  anchorId,
  anchorDate,
  anchorBalance,
  baselineDate,
  baselineBalance,
  baselineResolved,
  transactions,
  windowCovered,
  loadingTransactions,
  selectedIds,
  onSeedSelection,
  onReconciled,
}: Args<T>) => {
  const eligible = transactions.filter((t) => t.reconciliation == null);
  const needsStartingBalance = baselineResolved && baselineDate == null;

  // Keyed by anchor id so switching the session to another anchor re-seeds.
  const [seededAnchorId, setSeededAnchorId] = React.useState<string | null>(
    null,
  );
  // Reset the seed the moment the anchor changes — including when a session
  // ends and the same anchor is later reconciled again (the in-place timeline
  // keeps this hook mounted across sessions, so the seed must be cleared here
  // rather than by unmounting). Adjusting state during render is the idiomatic
  // way to derive from a changing input without an extra effect + commit.
  const [lastAnchorId, setLastAnchorId] = React.useState(anchorId);
  if (anchorId !== lastAnchorId) {
    setLastAnchorId(anchorId);
    setSeededAnchorId(null);
  }
  const hasSeeded = seededAnchorId === anchorId;
  React.useEffect(() => {
    if (
      hasSeeded ||
      !baselineResolved ||
      needsStartingBalance ||
      loadingTransactions ||
      !windowCovered
    ) {
      return;
    }
    setSeededAnchorId(anchorId);
    onSeedSelection(
      eligible.filter((t) =>
        inReconcileWindow({baselineDate, anchorDate}, t.date),
      ),
    );
  }, [
    hasSeeded,
    baselineResolved,
    needsStartingBalance,
    loadingTransactions,
    windowCovered,
    anchorId,
    eligible.length,
  ]);

  // Derive the submitted set from the loaded list so it always matches the
  // displayed sum (the selection may hold rows that later re-fetched as
  // reconciled).
  const selectedIdSet = new Set(selectedIds);
  const selectedTransactions = eligible.filter((t) =>
    selectedIdSet.has(t._id),
  );
  const selectedSum = selectedTransactions.reduce((s, t) => s + t.amount, 0);

  const needed = anchorBalance - baselineBalance;
  // The balance the selection would produce if it were the complete set of
  // activity for the period. diff === expectedBalance - anchorBalance.
  const expectedBalance = baselineBalance + selectedSum;
  const diff = selectedSum - needed;
  const isBalanced = Math.round(diff) === 0;
  // The signed remainder still unaccounted for — what a "fix up" adjustment
  // transaction would need to be so the selection lands exactly on `needed`.
  const remaining = needed - selectedSum;

  // Until the default selection is seeded the sums are partial (pages are
  // still streaming in), so summaries should show placeholders instead of
  // flashing misleading "off" amounts that jump once loading settles.
  const summaryPending = !hasSeeded && !needsStartingBalance;

  const [reconcile, {loading: submitting}] =
    useReconcileBalanceAnchorMutation();
  const [error, setError] = React.useState<string | null>(null);

  const canSubmit =
    !submitting &&
    baselineResolved &&
    !needsStartingBalance &&
    windowCovered &&
    hasSeeded &&
    isBalanced &&
    (selectedTransactions.length > 0 || needed === 0);

  const submit = async (): Promise<boolean> => {
    setError(null);
    const claimedIds = selectedTransactions.map((t) => t._id);
    try {
      await reconcile({
        variables: {
          anchorId,
          accountId,
          rawTransactionIds: claimedIds,
        },
      });
      onReconciled?.(claimedIds);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reconciliation failed');
      return false;
    }
  };

  // "Fix up" is only meaningful once the period is fully loaded and seeded but
  // still off by a nonzero remainder — the whole point is to absorb that gap.
  // The action itself (opening a prefilled create-transaction modal) lives in
  // the UI; the hook only decides when it applies and exposes the amount.
  const canFixUp =
    !submitting &&
    baselineResolved &&
    !needsStartingBalance &&
    windowCovered &&
    hasSeeded &&
    !summaryPending &&
    !isBalanced;

  return {
    eligible,
    selectedTransactions,
    selectedSum,
    needed,
    remaining,
    expectedBalance,
    diff,
    isBalanced,
    hasSeeded,
    summaryPending,
    needsStartingBalance,
    canSubmit,
    submit,
    canFixUp,
    submitting,
    error,
  };
};
